import { Configuration, SuppliersApi } from '@/api';
import CustomToast from '@/components/CustomToast';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import StoreForm from '@/modules/supplier/components/StoreForm';
import { StoreAddOrUpdate } from '@/modules/supplier/models/Store';
import { CustomerStoreListPath } from '@/modules/supplier/paths';
import { BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const StoreAdd = () => {
  const queryClient = useQueryClient();

  const invalidateStoreQueries = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => {
        const key = query.queryKey;
        console.log(key, Array.isArray(key) && key[0] === 'store');
        return Array.isArray(key) && key[0] === 'store';
      },
    });
  };

  const { currentUser } = useAuth();

  const supplierId = currentUser()?.supplierId;

  const navigate = useNavigate();

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const addStoreToSupplier = async (store: StoreAddOrUpdate, supplierId: string) => {
    await suppliersApi.addStoresToSupplier(supplierId, [
      {
        id: store.id,
        customerCode: store.customerCode,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Store added successfully!" visible={t.visible} />
    ));
  };

  const removeStoreFromSupplier = async (store: StoreAddOrUpdate, supplierId: string) => {
    await suppliersApi.removeStoresFromSupplier(supplierId, [
      {
        id: store.id,
        customerCode: store.customerCode,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Store removed successfully!" visible={t.visible} />
    ));
  };

  const handleSubmit = async (store: StoreAddOrUpdate) => {
    if (!supplierId) return;

    const shouldAddStore = store.customerCode !== '';

    try {
      if (shouldAddStore) {
        await addStoreToSupplier(store, supplierId);
      } else {
        await removeStoreFromSupplier(store, supplierId);
      }

      await invalidateStoreQueries();

      navigate(CustomerStoreListPath.withCustomerId(store.retailerId));
    } catch (error) {
      console.error('Error while submitting store:', error);
    }
  };

  return (
    <>
      <SettingsHeader title="Add Store" icon={<BuildingStorefrontIcon className="w-6 h-6" />} />

      <StoreForm onSubmit={handleSubmit} />
    </>
  );
};

export default StoreAdd;
